import { useTranslation } from 'react-i18next';
import {
  Button,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';
import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { IVacancy } from 'src/pages/vacancies/vacancies.interfaces';
import PermissionCheck from 'src/components/permission-check';

import {
  HeaderContainer,
  VacancyDetailsInfoWrapper,
  InfoBlock,
  InfoElement,
  InfoWrapper,
  NeededSkillsListElement,
  NeededSkillsListWrapper,
  ImageWrapper,
} from './vacancy-details-info.styles';

const VacancyDetailsInfo = ({
  vacancy,
  onClickEditBtn,
}: {
  vacancy: IVacancy;
  onClickEditBtn: () => void;
}) => {
  const { t } = useTranslation(['common', 'vacancies', 'jobs']);

  return (
    <VacancyDetailsInfoWrapper>
      <HeaderContainer>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('vacancies:labels.vacancyDetails')}
        </Typography>

        <PermissionCheck
          action={PERMISSION_ACTIONS.update}
          permission={PLATFORM_PERMISSIONS.vacanciesManagement}
          createdBy={vacancy.createdBy}
        >
          <Button
            type="primary"
            ghost
            icon={ICONS.editAlt}
            iconColor={PLATFORM_THEME_COLORS.primary.blue}
            onClick={onClickEditBtn}
          >
            {t('common:buttons.edit')}
          </Button>
        </PermissionCheck>
      </HeaderContainer>
      <InfoWrapper>
        <InfoBlock>
          <InfoElement>
            <Typography weight="bold">{`${t('common:labels.company')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {vacancy.company.name}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('common:labels.department')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {vacancy.department.name}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('common:labels.jobTitle')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {vacancy.job.title}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('common:labels.location')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {vacancy.jobLocation.companyLocation.name}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('vacancies:labels.responsiblePersons')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {vacancy.responsiblePersons.map(item => item.profile.displayName).join(', ')}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('common:labels.status')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {t(`vacancies:labels.statuses.${vacancy.status.toLowerCase()}`)}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('jobs:labels.jobDescription')}:`}</Typography>
            <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
              {vacancy.jobDescription || '-'}
            </Typography>
          </InfoElement>
          <InfoElement>
            <Typography weight="bold">{`${t('vacancies:labels.uploadHeaderImage')}:`}</Typography>
            {vacancy.headerImage ? (
              <ImageWrapper>
                <Icon
                  icon={ICONS.imageAlt}
                  size={16}
                  color={PLATFORM_THEME_COLORS.primary.grey}
                  rightMargin={false}
                />
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {vacancy.headerImage.filename}
                </Typography>
              </ImageWrapper>
            ) : (
              '-'
            )}
          </InfoElement>
        </InfoBlock>
        <InfoElement>
          <Typography weight="bold">{`${t('jobs:labels.neededSkills')}:`}</Typography>
          <NeededSkillsListWrapper>
            {vacancy.skills.length > 0
              ? vacancy.skills.map(({ name }, index) => (
                  <NeededSkillsListElement key={index}>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{name}</Typography>
                  </NeededSkillsListElement>
                ))
              : '-'}
          </NeededSkillsListWrapper>
        </InfoElement>
      </InfoWrapper>
    </VacancyDetailsInfoWrapper>
  );
};

export default VacancyDetailsInfo;
